(function () {
  /* Slideshow video is desktop-only. Below 750px sections/slideshow.liquid hides the whole
     video slide (poster included) and renders no mobile <video>, and getActiveVideo()
     returns nothing, so no video URL is ever requested on a phone. This used
     to fall back to the first (hidden) video when none was visible -- with a desktop-only
     markup that fallback would have downloaded the desktop file on mobile, so it is gone.
     Same breakpoint as the section's max-width: 749px rule; change both together. */
  var DESKTOP_VIDEO = window.matchMedia('(min-width: 750px)');

  /* The first sync() is what reaches play() -> ensureVideoLoaded() -> video.load(). Firing
     that at DOMContentLoaded put a multi-megabyte download in front of the hero poster. The
     poster IS the LCP element (img.slideshow__video-poster-img), so the video was starving
     the very thing LCP is measured on.

     Measured back when phones still got a video, 412px, 1.6Mbps / 4x CPU, 3 runs, median:
       video requested at DOMContentLoaded : FCP 2704ms  LCP 9292ms
       video request blocked entirely      : FCP 1532ms  LCP 5312ms

     afterLoadIdle keeps the behaviour identical -- same autoplay, same slide sync, and the
     poster is already on screen -- it only moves the bytes behind the paint. The idle
     timeout still starts it on a page that never goes idle, and the readyState check covers
     load having already fired. Anything user-initiated (slider scroll, prev/next buttons,
     visibilitychange) still calls sync() directly, so an early interaction never waits. */
  function afterLoadIdle(fn) {
    function run() {
      if ('requestIdleCallback' in window) requestIdleCallback(fn, { timeout: 2000 });
      else setTimeout(fn, 200);
    }
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  }

  function isVisible(el) {
    return !!(el && el.offsetParent !== null);
  }

  function canAutoPlay(component, slider) {
    if (!slider || slider.dataset.autoplay !== 'true') return false;
    if (component.sliderAutoplayButton) return !!component.autoplayButtonIsSetToPlay;
    return true;
  }

  function SlideshowVideoController(component) {
    this.component = component;
    this.slider = component.querySelector('[id^="Slider-"]');
    if (!this.slider) return;

    this.endAction = this.slider.dataset.videoEndAction || 'next_slide';
    this.videos = Array.prototype.slice.call(this.slider.querySelectorAll('.slideshow__video-player'));
    if (!this.videos.length) return;

    this.sync = this.sync.bind(this);
    this.onVideoEnded = this.onVideoEnded.bind(this);

    this.videos.forEach(function (video) {
      video.muted = true;
      video.playsInline = true;
      video.preload = 'none';
      video.setAttribute('playsinline', 'playsinline');
      video.addEventListener('ended', this.onVideoEnded);
    }, this);

    this.component.addEventListener('slideChanged', this.sync);
    this.slider.addEventListener(
      'scroll',
      function () {
        clearTimeout(this._syncTimer);
        this._syncTimer = setTimeout(this.sync, 90);
      }.bind(this),
      { passive: true }
    );

    if (this.component.prevButton) {
      this.component.prevButton.addEventListener('click', function () {
        requestAnimationFrame(this.sync);
      }.bind(this));
    }

    if (this.component.nextButton) {
      this.component.nextButton.addEventListener('click', function () {
        requestAnimationFrame(this.sync);
      }.bind(this));
    }

    document.addEventListener(
      'visibilitychange',
      function () {
        if (document.hidden) {
          this.pauseAllVideos();
        } else {
          this.sync();
        }
      }.bind(this)
    );

    /* Crossing the breakpoint (rotating a tablet, resizing a window): dropping below it
       pauses the video and hands the slide back to the slider's autoplay; going above it
       starts the video, loading it for the first time if this page began on mobile.
       The resize usually scrolls the slider too, so this shares the scroll handler's
       _syncTimer: two back-to-back syncs made the second one rewind the still-loading video
       to 0 and request the mp4 again. */
    var onBreakpoint = function () {
      clearTimeout(this._syncTimer);
      this._syncTimer = setTimeout(this.sync, 90);
    }.bind(this);
    if (DESKTOP_VIDEO.addEventListener) DESKTOP_VIDEO.addEventListener('change', onBreakpoint);
    else if (DESKTOP_VIDEO.addListener) DESKTOP_VIDEO.addListener(onBreakpoint);

    afterLoadIdle(this.sync);
  }

  SlideshowVideoController.prototype.getActiveSlide = function () {
    /* global.js's aria-hidden="false" is only trusted for a slide that is rendered and in
       view. It only re-marks the slides it renders, so across a 750px crossing the marks go
       stale or missing: the video slide (display:none on mobile) keeps a "false" it had on
       desktop, or nothing is marked at all. Either way a blind querySelector or a fall back
       to slide 1 played slide 1's video off-screen while the slider sat paused on whatever
       slide was actually showing. So: the marked slide if any of it is in view, otherwise
       the rendered slide nearest the slider's left edge. A stale mark is always a whole
       slide width away, so "any of it" is enough to reject it. Half-in-view was too strict:
       global.js fires slideChanged mid-scroll before it re-marks, so after a video ended the
       outgoing slide read as inactive and its video froze partway through the slide-out,
       where it has always kept playing until the scroll settles. At scrollLeft 0 with no
       marks yet this is slide 1, so a normal page load is unchanged. */
    var slides = this.slider.querySelectorAll('.slideshow__slide');
    var left = this.slider.getBoundingClientRect().left;
    var best = slides[0];
    var bestDistance = Infinity;
    for (var i = 0; i < slides.length; i += 1) {
      var rect = slides[i].getBoundingClientRect();
      if (!rect.width) continue;
      var distance = Math.abs(rect.left - left);
      if (slides[i].getAttribute('aria-hidden') === 'false' && distance < rect.width) return slides[i];
      if (distance < bestDistance) {
        best = slides[i];
        bestDistance = distance;
      }
    }
    return best;
  };

  SlideshowVideoController.prototype.getActiveVideo = function (slide) {
    if (!slide || !DESKTOP_VIDEO.matches) return null;
    var candidates = Array.prototype.slice.call(slide.querySelectorAll('.slideshow__video-player'));
    for (var i = 0; i < candidates.length; i += 1) {
      if (isVisible(candidates[i])) return candidates[i];
    }
    return null;
  };

  SlideshowVideoController.prototype.pauseAllVideos = function (exceptVideo) {
    this.videos.forEach(function (video) {
      if (video !== exceptVideo && !video.paused) {
        video.pause();
      }
    });
  };

  SlideshowVideoController.prototype.ensureVideoLoaded = function (video) {
    if (!video || video.dataset.lazyLoaded === 'true') return;

    var sourceNodes = Array.prototype.slice.call(video.querySelectorAll('source[data-src]'));
    if (sourceNodes.length) {
      sourceNodes.forEach(function (source) {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
    } else if (video.dataset.src) {
      video.src = video.dataset.src;
      video.removeAttribute('data-src');
    }

    video.dataset.lazyLoaded = 'true';
    video.load();
  };

  SlideshowVideoController.prototype.playVideo = function (video) {
    if (!video) return;

    this.ensureVideoLoaded(video);

    if (video.readyState < 2) {
      video.addEventListener(
        'loadeddata',
        function () {
          var retry = video.play();
          if (retry && typeof retry.catch === 'function') {
            retry.catch(function () {});
          }
        },
        { once: true }
      );
    }

    var promise = video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(function () {});
    }
  };

  SlideshowVideoController.prototype.resumeSliderAutoplay = function () {
    if (typeof this.component.play === 'function' && canAutoPlay(this.component, this.slider)) {
      this.component.play();
    }
  };

  SlideshowVideoController.prototype.pauseSliderAutoplay = function () {
    if (typeof this.component.pause === 'function') {
      this.component.pause();
    }
  };

  SlideshowVideoController.prototype.sync = function () {
    var activeSlide = this.getActiveSlide();
    var activeVideo = this.getActiveVideo(activeSlide);

    this.pauseAllVideos(activeVideo);

    if (!activeVideo) {
      this.resumeSliderAutoplay();
      return;
    }

    this.pauseSliderAutoplay();

    if (activeVideo.currentTime > 0 && !activeVideo.ended && !activeVideo.paused) {
      return;
    }

    activeVideo.currentTime = 0;
    this.playVideo(activeVideo);
  };

  SlideshowVideoController.prototype.goToNextSlide = function () {
    if (this.component.nextButton) {
      this.component.nextButton.click();
      return;
    }

    var step = this.component.sliderItemOffset || this.slider.clientWidth;
    var isLastSlide = this.component.currentPage === this.component.totalPages;
    var nextPosition = isLastSlide ? 0 : this.slider.scrollLeft + step;
    this.slider.scrollTo({ left: nextPosition });
  };

  SlideshowVideoController.prototype.onVideoEnded = function (event) {
    var endedVideo = event.currentTarget;
    var activeSlide = this.getActiveSlide();
    if (!activeSlide || !activeSlide.contains(endedVideo)) return;

    if (this.endAction === 'replay_video' || this.component.totalPages <= 1) {
      endedVideo.currentTime = 0;
      this.playVideo(endedVideo);
      return;
    }

    this.goToNextSlide();
    setTimeout(this.sync, 120);
  };

  function initSlideshowVideoControllers() {
    var components = document.querySelectorAll('slideshow-component[data-has-video-slides="true"]');
    components.forEach(function (component) {
      if (component.__slideshowVideoController) return;
      component.__slideshowVideoController = new SlideshowVideoController(component);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSlideshowVideoControllers);
  } else {
    initSlideshowVideoControllers();
  }

  document.addEventListener('shopify:section:load', initSlideshowVideoControllers);
})();
